import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { HhsgSharedAdminTab } from '../../../models/admin-tab.model';
import { HhsgSharedLoadingService, HhsgSharedTabControlService, HhsgSharedUserService } from '../../../services';
import { Constants } from '../../../utilities/constants.util';
import { HhsgSharedSelectOption } from '../../../models/dropdown.model';
import { HhsgSharedEllipseSelectOption } from '../../../models/ellipses.model';
import { DevExtremeUtils } from '../../../utilities/dev-extreme.util';
import dxSelectBox from 'devextreme/ui/select_box';
import { NavigationStart, Router ,Event} from '@angular/router';

@Component({
    selector: 'hhsg-tab-control',
    templateUrl: './tab-control.component.html',
    styleUrls: ['./tab-control.component.scss']
})
export class HhsgSharedTabControlComponent implements AfterViewChecked, OnChanges, OnDestroy, OnInit {
    @Input() headerLabel: string;
    @Input() subHeaderLabel: string;
    @Input() tabs: HhsgSharedAdminTab[] = [];
    @Input() headerButtonText = "";
    @Input() ellipsesMenuItems: HhsgSharedEllipseSelectOption[] = [];
    @Input() isSaveTab:boolean=true; 
    @Input() dropDownMenuItems: HhsgSharedSelectOption[];
    @Input() selectedDropdownValue: string;
    @Input() dropDownName: string = "";
    @Input() dropDownToolTipText: string = "";
    @Input() isDropDownDisabled:boolean=false;
    @Input() allowMobile:boolean=false;
    @Input() showBreadCrumb: boolean = false;
    @Input() messageBarOffset: number;
    @Input() showInactive: boolean = false;
    @Output() saveButtonClicked = new EventEmitter();
    @Output() cancelButtonClicked = new EventEmitter();
    @Output() headerButtonClicked = new EventEmitter();
    @Output() ellipsesButtonClicked = new EventEmitter();
    @Output() dropDownOptionChanged = new EventEmitter();

    public displayDeactivation = false;
    public errorMessages: string[] = [];
    public pendingTab: HhsgSharedAdminTab;
    public selectedTab: HhsgSharedAdminTab;
    public useHeaderButton = false;
    public useHeaderLabel = false;
    public mobileTabCollapse = true;
    public showTooltip = false;
    public dxStylingMode: string = DevExtremeUtils.DevExtremeStyleMode;

    private _tabSubscription = new Subscription;
    private _unsubscribe$ = new Subject<void>();

    constructor(
        private _changeDetector: ChangeDetectorRef,
        private _loadingService: HhsgSharedLoadingService,
        private _tabControlService: HhsgSharedTabControlService,
        private  _userService: HhsgSharedUserService,
        private _router: Router) {
        this._tabSubscription.add(
            this._tabControlService.tab$
                .pipe(takeUntil(this._unsubscribe$))
                .subscribe(tab => {
                    let foundTab = this.tabs.find(t => t.id == tab.id);

                    if (foundTab) {
                        foundTab.isDirty = tab.isDirty;
                        foundTab.isDisabled = tab.isDisabled;
                    }
                })
        );

        this._tabSubscription.add(
            this._tabControlService.tabHidden$
                .pipe(takeUntil(this._unsubscribe$))
                .subscribe(tabName => {
                    let foundTab = this.tabs.find(t => t.tabName == tabName);

                    if (foundTab) {
                        foundTab.isVisible = false;
                        this.updateLoadingStatus();
                    }
                })
        );

        this._tabSubscription.add(
            this._tabControlService.tabSelectedChange$
                .pipe(takeUntil(this._unsubscribe$))
                .subscribe(tabId => {
                    let foundTab = this.tabs.find(t => t.id == tabId);
                    this.selectTab(foundTab);
                })
        );

        this._tabSubscription.add(
            this._tabControlService.tabVisible$
                .pipe(takeUntil(this._unsubscribe$))
                .subscribe(tabName => {
                    let foundTab = this.tabs.find(t => t.tabName == tabName);

                    if (foundTab) {
                        foundTab.isVisible = true;
                        this.updateLoadingStatus();
                    }
                })
        );

        this._tabSubscription.add(
            this._tabControlService.tabLoading$
                .pipe(takeUntil(this._unsubscribe$))
                .subscribe(tabId => {
                    let foundTab = this.tabs.find(t => t.id == tabId);

                    if (foundTab) {
                        foundTab.isLoading = true;
                        this.updateLoadingStatus();
                    }
                })
        );

        this._tabSubscription.add(
            this._tabControlService.tabFinishedLoading$
                .pipe(takeUntil(this._unsubscribe$))
                .subscribe(tabId => {
                    let foundTab = this.tabs.find(t => t.id == tabId);

                    if (foundTab) {
                        foundTab.isLoading = false;
                        this.updateLoadingStatus();
                    }
                })
        );

        this._router.events.subscribe((event: Event) => {
            if (event instanceof NavigationStart) {
               this.setDefaultValueForTypeDropdown();
            }
        });
    }

    ngAfterViewInit() {
        this.setDefaultValueForTypeDropdown();
    }

    // Prevent "Expression has changed after it was checked" error
    public ngAfterViewChecked(): void {
        this._changeDetector.detectChanges();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.useHeaderLabel = this.headerLabel &&  this.headerLabel > "";
        if (!this.selectedTab && this.tabs && this.tabs.length > 0) {
            this.selectedTab = this.tabs[0];
        }

        let foundTab = this.tabs.find(t => t.isSelected);

        if (foundTab) {
            this.selectTab(foundTab);
        }
    }

    public ngOnDestroy(): void {
        if (this._tabSubscription) {
            this._tabSubscription.unsubscribe();
        }

        this._unsubscribe$.next();
        this._unsubscribe$.complete();
    }

    public ngOnInit(): void {
        this.useHeaderButton = this.headerButtonText && this.headerButtonText > "";
        this.useHeaderLabel = this.headerLabel && this.headerLabel > "";
    }

    public get hasErrorMessages(): boolean {
        return this.errorMessages && this.errorMessages.length > 0 && this.errorMessages.some(s => s !== Constants.SaveSuccess);
    }

    public onCancelClicked(): void {
        this.cancelButtonClicked.emit();
    }

    public onHeaderButtonClicked(): void {
        this.headerButtonClicked.emit();
    }
    
    public onEllipsisButtonClicked(selectedOption: string): void {
        this.ellipsesButtonClicked.emit(selectedOption);
    }

    public onDeactivateLeaveTab(): void {
        if (!this.pendingTab) {
            return;
        }

        this.selectTab(this.pendingTab);
        this.pendingTab = null;
        this.displayDeactivation = false;
    }

    private selectTab(tab: HhsgSharedAdminTab) {
        this.tabs.forEach(t => {
            t.isSelected = t.id == tab.id
        });
        this.selectedTab = tab;
        this._tabControlService.announceSelectedTab(this.selectedTab.id);
    }

    public onSaveClicked(): void {
        this.saveButtonClicked.emit();
    }

    public onTabClick(event: any, tab: HhsgSharedAdminTab): void {
        this.mobileTabCollapse = true;
        if (this.selectedTab.isDirty) {
            event.preventDefault();
            this.pendingTab = tab;
            this.displayDeactivation = true;
        }
        else {
            this.pendingTab = null;
            this.selectTab(tab);
            this._tabControlService.announceSelectedTabChange(tab.id);
        }
    }

    public setErrorMessages(errors: string[]): void {
        this.errorMessages = errors;
    }

    private updateLoadingStatus(): void {
        // Only turn on loading if we have have tabs and the tab that is loading is currently selected.
        this._loadingService.setLoading(
            this.visibleTabs &&
            this.visibleTabs.length > 0 &&
            this.selectedTab &&
            this.visibleTabs.some(h => h.isLoading && h.id == this.selectedTab.id));
    }

    public get visibleTabs(): HhsgSharedAdminTab[] {
        return this.tabs.filter(h => h.isVisible);
    }

    public onChangeDropdownOption(event: any): void {
        this.dropDownOptionChanged.emit(event.value);
    }

    toggleTooltip() {
        this.showTooltip = !this.showTooltip;
    }

   private setDefaultValueForTypeDropdown() {
    if (this._userService.convertChildApplicationUserToHapUser && this._userService.childApplicationUser) {
        let element = document.getElementById("dropDownValue");
        let instance = dxSelectBox.getInstance(element) as dxSelectBox;
        instance?.option("value", "1");
        instance?.option("disabled", true);
    }
   }
}
